import type { HomeApiData } from '@/services/webApi'

const CACHE_VERSION = 'v3-city-images'
const TTL_MS = 10 * 60_000

interface HomeCacheEntry {
  data: HomeApiData
  updatedAt: number
}

function cacheKey(cityId: number | null) {
  return `${CACHE_VERSION}:home:${cityId ?? 'all'}`
}

/** Session-scoped homepage API payload — instant paint on repeat visits within the tab. */
export function readHomeApiCache(cityId: number | null): HomeCacheEntry | undefined {
  if (typeof sessionStorage === 'undefined') return undefined

  try {
    const raw = sessionStorage.getItem(cacheKey(cityId))
    if (!raw) return undefined

    const entry = JSON.parse(raw) as HomeCacheEntry
    if (!entry?.data || typeof entry.updatedAt !== 'number') return undefined
    if (Date.now() - entry.updatedAt > TTL_MS) {
      sessionStorage.removeItem(cacheKey(cityId))
      return undefined
    }

    return entry
  } catch {
    return undefined
  }
}

export function writeHomeApiCache(cityId: number | null, data: HomeApiData) {
  if (typeof sessionStorage === 'undefined') return

  try {
    const entry: HomeCacheEntry = { data, updatedAt: Date.now() }
    sessionStorage.setItem(cacheKey(cityId), JSON.stringify(entry))
  } catch {
    // Storage full or private mode — ignore.
  }
}
